import React, { useEffect, useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
export type CarouselMedia = {
  src: string;
  alt: string;
  caption?: string;
  isVideo?: boolean;
};
type MediaCarouselProps = {
  label: string;
  items: CarouselMedia[];
};

/**
 * A controlled media viewer for a small, related set of case-study screens.
 * It intentionally does not auto-advance: visitors set the pace while reviewing work.
 */
export function MediaCarousel({
  label,
  items
}: MediaCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const reduceMotion = useReducedMotion();
  const carouselId = useId();

  // Warm the browser cache for every item up front (not just the active one) so
  // advancing to the next slide never has to wait on a fresh network request —
  // the visible jank was caused by images/videos only starting to load once
  // they became active.
  useEffect(() => {
    const preloadedVideos: HTMLVideoElement[] = [];
    items.forEach(item => {
      if (item.isVideo) {
        const video = document.createElement('video');
        video.preload = 'auto';
        video.muted = true;
        video.src = item.src;
        preloadedVideos.push(video);
      } else {
        const image = new Image();
        image.src = item.src;
      }
    });
    return () => {
      preloadedVideos.forEach(video => {
        video.src = '';
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (items.length === 0) return null;
  const activeItem = items[activeIndex];
  const hasMultipleItems = items.length > 1;
  const goTo = (nextIndex: number) => {
    const normalizedIndex = (nextIndex + items.length) % items.length;
    setDirection(normalizedIndex > activeIndex || activeIndex === items.length - 1 && normalizedIndex === 0 ? 1 : -1);
    setActiveIndex(normalizedIndex);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!hasMultipleItems) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(activeIndex + 1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(activeIndex - 1);
    }
  };
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    const SWIPE_DISTANCE = 50;
    const SWIPE_VELOCITY = 400;
    if (info.offset.x < -SWIPE_DISTANCE || info.velocity.x < -SWIPE_VELOCITY) {
      goTo(activeIndex + 1);
    } else if (info.offset.x > SWIPE_DISTANCE || info.velocity.x > SWIPE_VELOCITY) {
      goTo(activeIndex - 1);
    }
  };
  return <section aria-labelledby={`${carouselId}-label`} className="my-10 md:my-14" onKeyDown={handleKeyDown} data-id="element-325" data-mp="3hz5ycddz7uu4">
      <div className="mb-4 flex items-end justify-between gap-5" data-id="element-326" data-mp="119qy21f4s7rw">
        <div data-id="element-327" data-mp="x8xchep5v526">
          <p id={`${carouselId}-label`} className="font-display text-xs font-medium uppercase tracking-[0.1em] text-muted" data-id="element-328" data-mp="x9gj3z9d270w">
            {label}
          </p>
          {hasMultipleItems && <p className="mt-1 text-sm text-muted" aria-live="polite" data-id="element-329" data-mp="19r9ji7bbi7p6">
              {activeIndex + 1} of {items.length}
            </p>}
        </div>

        {hasMultipleItems && <div className="flex items-center gap-2" aria-label={`${label} controls`} data-id="element-330" data-mp="zt3fnk5yarho">
            <button type="button" onClick={() => goTo(activeIndex - 1)} aria-label={`Show previous ${label.toLowerCase()} image`} className="inline-flex h-10 w-10 items-center justify-center border border-border text-foreground transition-[border-color,color,background-color] duration-200 hover:border-cobalt hover:bg-cobalt hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cobalt focus-visible:ring-offset-2" data-id="element-331" data-mp="zv6durh9bqxh">
              <ArrowLeftIcon size={17} aria-hidden="true" data-id="element-332" data-mp="2b88nf2kxvg85" />
            </button>
            <button type="button" onClick={() => goTo(activeIndex + 1)} aria-label={`Show next ${label.toLowerCase()} image`} className="inline-flex h-10 w-10 items-center justify-center border border-border text-foreground transition-[border-color,color,background-color] duration-200 hover:border-cobalt hover:bg-cobalt hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cobalt focus-visible:ring-offset-2" data-id="element-333" data-mp="2bacvlwpvf1e8">
              <ArrowRightIcon size={17} aria-hidden="true" data-id="element-334" data-mp="11bvd6nf729dt" />
            </button>
          </div>}
      </div>

      <div className="overflow-hidden" tabIndex={hasMultipleItems ? 0 : undefined} aria-roledescription="carousel" data-id="element-335" data-mp="wk3ji40qrnjg">
        <AnimatePresence initial={false} mode="wait" data-id="element-336" data-mp="rkqupdqb86e8">
          <motion.figure key={activeItem.src} initial={reduceMotion ? false : {
          opacity: 0,
          x: direction * 24
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={reduceMotion ? undefined : {
          opacity: 0,
          x: direction * -24
        }} transition={{
          duration: 0.26,
          ease: [0.25, 1, 0.5, 1]
        }} drag={hasMultipleItems ? 'x' : false} dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.7} onDragEnd={handleDragEnd} style={{
          touchAction: 'pan-y'
        }} className={hasMultipleItems ? 'cursor-grab active:cursor-grabbing' : undefined} data-id="element-337" data-mp="1p166jju7t1hx">
            {activeItem.isVideo ? <video src={activeItem.src} aria-label={activeItem.alt} className="block h-auto w-full" autoPlay loop muted playsInline preload="metadata" data-id="element-338" data-mp="2bg53vgrgvnec" /> : <img src={activeItem.src} alt={activeItem.alt} className="block h-auto w-full" loading="lazy" decoding="async" data-id="element-339" data-mp="2u8fofui96ynn" />}
            {activeItem.caption && <figcaption className="mt-3 flex items-start gap-3 text-xs font-medium uppercase leading-relaxed tracking-[0.08em] text-muted" data-id="element-340" data-mp="29haihhw6b35q">
                <span aria-hidden="true" className="mt-[0.45rem] h-px w-5 shrink-0 bg-cobalt" data-id="element-341" data-mp="2j9q20jn4bzsq" />
                <span data-id="element-342" data-mp="2ehy5lsha6zuo">{activeItem.caption}</span>
              </figcaption>}
          </motion.figure>
        </AnimatePresence>
      </div>

      {hasMultipleItems && <div className="mt-4 flex gap-2" role="tablist" aria-label={`Choose a ${label.toLowerCase()} image`} data-id="element-343" data-mp="2nateqx9vdfpq">
          {items.map((item, index) => <button key={item.src} type="button" role="tab" aria-selected={index === activeIndex} aria-label={`Show image ${index + 1}: ${item.caption ?? item.alt}`} onClick={() => goTo(index)} className={`h-1.5 flex-1 transition-[background-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cobalt focus-visible:ring-offset-2 ${index === activeIndex ? 'bg-cobalt' : 'bg-border hover:bg-muted'}`} data-id="element-344" data-mp="327k56h1kfh37" data-mpi={index} />)}
        </div>}
    </section>;
}